import clsx from 'clsx';
import { type PropsWithChildren, useEffect, useState } from 'react';

import { Flex } from '../../../../../layouts/flex/Flex.js';
import { interactionsOverlay } from '../../../../core/components/interactions-overlay/InteractionsOverlay.sty.js';
import { useOverlay } from '../../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { useTooltipData } from '../../../../core/components/overlay-tooltip/hooks/useTooltipData.js';
import { OverlayTooltip } from '../../../../core/components/overlay-tooltip/OverlayTooltip.js';
import { isTooltipDataEmpty } from '../../../../core/components/overlay-tooltip/utils.js';
import { useSeriesActions } from '../../../../core/components/series-action/ChartSeriesActionContext.js';
import { MeterBarChartTooltip } from '../../../shared/components/meter-bar-tooltip/MeterBarChartTooltip.js';
import { useFormatter } from '../../../shared/context/FormatterContext.js';
import { useMeterBarLayoutSize } from '../../../shared/context/MeterBarLayoutSizeContext.js';
import { elementSize } from '../../../shared/styles/element-size.sty.js';
import { container } from '../../../shared/styles.sty.js';
import { getNumericValue } from '../../../shared/utils/get-numeric-value.js';
import { TOOLTIP_VARIANT } from '../../constants.js';
import { useMultiMeterBarChartTooltipData } from '../../context/MultiMeterBarChartTooltipContext.js';
import { useMultiMeterBarVisibleSegmentsState } from '../../hooks/store/selectors.js';
import { useMultiMeterBarChartTooltipHandlers } from '../../hooks/useMultiMeterBarChartTooltipHandlers.js';
import type { MultiMeterBarChartInternalTooltipConfig } from '../../types/multi-meter-bar-chart-internal.js';
import type { MultiMeterBarSegmentProps } from '../../types/multi-meter-bar-chart.js';

type CustomTooltipProps = {
  template: NonNullable<MultiMeterBarChartInternalTooltipConfig['template']>;
  visibleSegments: MultiMeterBarSegmentProps[];
  hoveredIndex: number | undefined;
};

const MultiMeterBarCustomTooltip = ({
  template,
  visibleSegments,
  hoveredIndex,
}: CustomTooltipProps) => {
  const segments = visibleSegments.map((s) => ({
    name: s.name ?? '',
    value: getNumericValue(s.value, s.valueAccessor),
    color: s.color ?? '',
  }));
  const activeSegment =
    hoveredIndex !== undefined && segments[hoveredIndex]
      ? { ...segments[hoveredIndex], index: hoveredIndex }
      : undefined;
  return (
    <OverlayTooltip legacyTemplate={template({ segments, activeSegment })} />
  );
};

export const MultiMeterBarChartOverlayWithTooltip = (
  props: PropsWithChildren,
) => {
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<
    number | undefined
  >(undefined);

  const overlay = useOverlay();
  const { chartSeriesActions } = useSeriesActions();
  const { tooltipConfig } = useMultiMeterBarChartTooltipData();
  const { size } = useMeterBarLayoutSize();
  const { formatter, unit } = useFormatter();

  const visibleSegments = useMultiMeterBarVisibleSegmentsState();

  useEffect(() => {
    setHoveredSegmentIndex(undefined);
  }, [visibleSegments.length]);

  const isSharedTooltip = tooltipConfig.variant === TOOLTIP_VARIANT.Shared;
  const sharedData = {
    geometry: 'multimeterbar' as const,
    series: visibleSegments.map((item) => ({
      name: item.name ?? '',
      value: formatter(item.value, unit),
      color: item.color,
    })),
  };

  const { onClick, onPointerMove, onPointerLeave } =
    useMultiMeterBarChartTooltipHandlers(sharedData, setHoveredSegmentIndex);

  const { data: tooltipData } = useTooltipData();

  const tooltipContent =
    tooltipConfig.template && !isTooltipDataEmpty(tooltipData) ? (
      <MultiMeterBarCustomTooltip
        template={tooltipConfig.template}
        visibleSegments={visibleSegments}
        hoveredIndex={hoveredSegmentIndex}
      />
    ) : (
      <MeterBarChartTooltip
        variant={
          isSharedTooltip ? TOOLTIP_VARIANT.Shared : TOOLTIP_VARIANT.Single
        }
        activeItem={hoveredSegmentIndex ?? -1}
        actionsForItem={
          chartSeriesActions
            ? (item) => () => chartSeriesActions(item)
            : undefined
        }
      />
    );

  return (
    <Flex
      ref={overlay.overlayRef}
      className={clsx(container, elementSize({ size }), interactionsOverlay)}
      data-testid="overlay-wrapper"
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
    >
      {props.children}
      {tooltipContent}
    </Flex>
  );
};
